import _slicedToArray from "@babel/runtime-corejs2/helpers/slicedToArray";
import _Object$assign from "@babel/runtime-corejs2/core-js/object/assign";
import _defineProperty from "@babel/runtime-corejs2/helpers/defineProperty";
import _objectSpread from "@babel/runtime-corejs2/helpers/objectSpread";
import _Object$keys from "@babel/runtime-corejs2/core-js/object/keys";
import _Number$isNaN from "@babel/runtime-corejs2/core-js/number/is-nan";
import React from 'react';
/**
 * Cloudinary base url
 * Used when a url is from cloudfront and we have to do the fetch
 */

var cloudinaryBaseUrl = 'https://res.cloudinary.com/dqzqcuqf9/image/';
/**
 * Cloudinary accepted transformations and the accepted format for validation
 * @important Remember to add the new parameters to the useCloudinary hook
 * as it uses the spread syntax's
 */

var paramsAndValidation = {
  /**
   * Width
   * @tutorial https://cloudinary.com/documentation/image_transformations#resizing_and_cropping_images
   * @requires param {number} 0 to 1 for % or > 1 for hard size
   */
  w: function w(param) {
    return !_Number$isNaN(param);
  },

  /**
   * Height
   * @tutorial https://cloudinary.com/documentation/image_transformations#resizing_and_cropping_images
   * @requires param {number} 0 to 1 for % or > 1 for hard size
   */
  h: function h(param) {
    return !_Number$isNaN(param);
  },

  /**
   * Crop applied
   * @tutorial https://cloudinary.com/documentation/image_transformations#resizing_and_cropping_images
   * @requires param {string} One of the values below
   */
  c: function c(param) {
    return ['scale', 'fit', 'limit', 'mfit', 'fill', 'lfill', 'pad', 'lpad', 'mpad', 'fill_pad', 'crop'].includes(param);
  },

  /**
   * Image quality
   * @tutorial https://cloudinary.com/documentation/image_transformations#adjusting_image_quality
   * @requires param {number|string} Between 1 and 100 or one of the strings below
   */
  q: function q(param) {
    return !_Number$isNaN(param) && param < 101 && param > 0 || ['auto', 'auto:good', 'auto:best', 'auto:eco', 'auto:low'].includes(param);
  },

  /**
   * Gravity aka the IA that decides the part of the picture to return
   * @tutorial https://cloudinary.com/documentation/image_transformations#control_gravity
   * @requires param {string} One of the values below
   */
  g: function g(param) {
    return ['auto', 'faces'].includes(param);
  },

  /**
   * Aspect ratio
   * @tutorial https://cloudinary.com/documentation/image_transformations#aspect_ratio_based_cropping
   * @requires param {string|number} With 12:3 format or as numeric
   */
  ar: function ar(param) {
    return param.split(':').length === 2 || !_Number$isNaN(param);
  },

  /**
   * Pixel ratio
   * @tutorial https://cloudinary.com/documentation/image_transformations#set_device_pixel_ratio_dpr
   * @requires param {number} With 12:3 format or as numeric
   */
  dpr: function dpr(param) {
    return !_Number$isNaN(param);
  },

  /**
   * Fetch format
   * @tutorial https://cloudinary.com/documentation/image_transformations#fetch_format
   * @requires param {string} One of the strings below
   */
  f: function f(param) {
    return ['auto', 'png', 'jpg', 'jpeg'].includes(param);
  }
};
/**
 * Default Cloudinary params object
 * Created from object declared in paramsAndValidation
 * @var {object}
 */

var defaultParams = _Object$keys(paramsAndValidation).reduce(function (object, key) {
  return _objectSpread({}, object, _defineProperty({}, key, null));
}, {});
/**
 * Initializes params in case they are missing in the object
 * @param {object} params
 * @returns {object}
 */


var initializeIfMissing = function initializeIfMissing(params) {
  return _Object$assign(defaultParams, params);
};
/**
 * Concatenates key and string as Cloudinary expected format
 * @param {string} param
 * @param {string} value
 * @returns {string}
 */


var createParam = function createParam(param, value) {
  return "".concat(param, "_").concat(value);
};
/**
 * Splits url by "/"
 * @param {string} url
 */


var splitUrl = function splitUrl(url) {
  return url.split('/');
};
/**
 * @param {string} url
 * @return {boolean}
 */


var urlIsFromCloudinary = function urlIsFromCloudinary(url) {
  return url.includes('res.cloudinary.com');
};
/**
 * Url piece includes params?
 * @param {string} pieceOfUrl
 * @return {boolean}
 */


var urlIncludesParams = function urlIncludesParams(pieceOfUrl) {
  return pieceOfUrl.includes('_');
};
/**
 * Extracts url string parameters as object
 * @param {string} pieceOfUrl
 * @return {object}
 */


var extractParamsFromUrl = function extractParamsFromUrl(pieceOfUrl) {
  return pieceOfUrl.split(',').reduce(function (object, propertyAndValue) {
    var param = propertyAndValue.split('_');
    return _objectSpread({}, object, _defineProperty({}, param[0], param[1]));
  }, {});
};
/**
 * Removes null params so they don't overwrite in case the url already has some by default
 * @param {object} params
 */


var removeNullParams = function removeNullParams(params) {
  _Object$keys(params).forEach(function (key) {
    if (params[key] === null) {
      /* eslint-disable no-param-reassign */
      delete params[key];
    }
  });

  return params;
};
/**
 * Creates the new url with the specified params
 * @param {string} url
 * @param {object} params
 */


var createUrl = function createUrl(url, params) {
  // If url is null escape execution here
  if (!url) {
    return {
      updatedUrl: null,
      errors: null
    };
  }

  var errors = [];
  var nUrl = url;
  var nParams = removeNullParams(params);
  /**
   * Sometimes the url cam be from cloudfront
   * if it is the case we place them after Cloudinary url and fetch method
   */

  if (!urlIsFromCloudinary(url)) {
    nUrl = "".concat(cloudinaryBaseUrl, "fetch/").concat(url);
  }

  var urlSplit = splitUrl(nUrl);

  if (!['upload', 'fetch'].includes(urlSplit[5])) {
    errors.push('The url has the wrong format, the expected url looks like this: ' + 'https://res.cloudinary.com/dqzqcuqf9/image/upload/v1596462825/mfp-fe-artworks/ynyloqa6jhdwaxk11swk.jpg - ' + 'The upload/fetch has to be in the fifth position of the array after url split by "/"');
  }

  if (urlIncludesParams(urlSplit[6])) {
    nParams = _Object$assign(extractParamsFromUrl(urlSplit[6]), nParams);
  }
  /**
   * Parameter validation and parameter array creation
   * --
   * We check if it is declared in paramsAndValidation
   * If not, we skip the validation we but still use it
   * these are the parameters that can come predefined in the url
   */


  var paramsAsArray = [];

  _Object$keys(nParams).forEach(function (key) {
    var value = nParams[key];

    if (value !== null) {
      if ('key' in paramsAndValidation && !paramsAndValidation[key](value)) {
        errors.push("Wrong value for ".concat(key, " parameter (").concat(value, " received)"));
        return false;
      }

      paramsAsArray.push(createParam(key, value));
    }

    return paramsAsArray;
  }); // Deal with url concatenation here


  urlSplit[5] += "/".concat(paramsAsArray.join(','));
  return {
    updatedUrl: urlSplit.join('/'),
    errors: errors.length ? errors : null
  };
};
/**
 * Hook: useCloudinary()
 * --
 * @description Given some parameters it performs the necessary transformations
 * in the url so that Cloudinary can process the request and return an image url
 * --
 * @aware The parameters specified in the params have priority over the parameters
 * (if included) in the url
 * @aware If url is null|undefined|0 it returns null as url
 * --
 * @param {string} url A valid Cloudinary or cloudfront url
 * @param {object} params The ones declared in paramsAndValidation()
 * @param {boolean} deferred If set to true generates the url at the client side,
 * is useful for images where we don't know the width until the client has loaded
 * the page as if not used it will create a blink effect because the server
 * has loaded an image that it is not equivalent to the one requested in the client
 * --
 * @returns {object}
 */


var useCloudinary = function useCloudinary(url) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultParams;
  var deferred = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var _React$useState = React.useState(deferred ? null : url),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      cUrl = _React$useState2[0],
      setCUrl = _React$useState2[1];

  var _initializeIfMissing = initializeIfMissing(params),
      w = _initializeIfMissing.w,
      h = _initializeIfMissing.h,
      c = _initializeIfMissing.c,
      q = _initializeIfMissing.q,
      g = _initializeIfMissing.g,
      ar = _initializeIfMissing.ar,
      dpr = _initializeIfMissing.dpr,
      f = _initializeIfMissing.f;

  var _React$useMemo = React.useMemo(function () {
    return createUrl(cUrl, {
      w: w,
      h: h,
      c: c,
      q: q,
      g: g,
      ar: ar,
      dpr: dpr,
      f: f
    });
  }, [cUrl, w, h, c, q, g, ar, dpr, f]),
      updatedUrl = _React$useMemo.updatedUrl,
      errors = _React$useMemo.errors;
  /**
   * React.useEffect is processed at client side
   * This is why is used in conjunction with deferred we can control
   * that the url that is returned is null until client is ready
   */


  React.useEffect(function () {
    if (deferred) {
      setCUrl(url);
    }
  }, [deferred, url]);
  return {
    url: updatedUrl,
    errors: errors
  };
};

export default useCloudinary;